import React, { useState } from 'react';
import { Download, X, Loader2 } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';
import { calculateReferralFee, calculateWFSFee } from '../utils';

const SCAN_TYPE_CONFIG = {
	'live-scan': {
		label: 'Live Scan',
		listUrl: `${API_BASE}/api/live-scan/groups`,
		listKey: 'groups',
		dealsUrl: (item) => `${API_BASE}/api/live-scan/groups/${item.id}/deals`,
		dealsKey: 'deals',
		itemName: (item) => item.group_name,
		itemNoun: 'groups',
		filename: 'live_scan_export',
		extraHeaders: ['Group Name', 'Supplier', 'Source Qty'],
		extraFields: (deal, item) => [item.group_name || '', deal.supplier_name || '', deal.source_qty || ''],
	},
	'reverse-scan': {
		label: 'Reverse Scan',
		listUrl: `${API_BASE}/api/reverse-scan/searches`,
		listKey: 'searches',
		dealsUrl: (item) => `${API_BASE}/api/reverse-scan/searches/${item.id}/deals`,
		dealsKey: 'deals',
		itemName: (item) => item.search_name,
		itemNoun: 'searches',
		filename: 'reverse_scan_export',
		extraHeaders: ['Search Name', 'Supplier'],
		extraFields: (deal, item) => [item.search_name || '', deal.supplier_name || ''],
	},
	'storefront': {
		label: 'Storefront',
		listUrl: `${API_BASE}/api/storefront/tracked`,
		listKey: 'storefronts',
		dealsUrl: (item) => `${API_BASE}/api/storefront/deals/${item.id}`,
		dealsKey: 'deals',
		itemName: (item) => item.store_name || item.storefront_url,
		itemNoun: 'storefronts',
		filename: 'storefront_export',
		extraHeaders: ['Store Name', 'Supplier', 'Storefront Price', 'Is New'],
		extraFields: (deal, item) => [
			item.store_name || item.storefront_url || '',
			deal.supplier_name || '',
			deal.storefront_price ? parseFloat(deal.storefront_price).toFixed(2) : '',
			deal.is_new ? 'Yes' : 'No',
		],
	},
	'wholesale': {
		label: 'Wholesale',
		listUrl: `${API_BASE}/api/wholesale/uploads`,
		listKey: 'uploads',
		dealsUrl: (item) => `${API_BASE}/api/wholesale/uploads/${item.id}/deals`,
		dealsKey: 'deals',
		itemName: (item) => item.upload_name,
		itemNoun: 'uploads',
		filename: 'wholesale_export',
		extraHeaders: ['Upload Name', 'File Name', 'Supplier', 'Is New'],
		extraFields: (deal, item) => [
			item.upload_name || '', item.file_name || '',
			deal.supplier_name || '',
			deal.is_new ? 'Yes' : 'No',
		],
	},
};

const COMMON_HEADERS = [
	'Marketplace', 'ASIN / Product ID', 'Title', 'UPC',
	'Source Price', 'Sell Price', 'Total Fees', 'Profit', 'ROI %',
	'Gated Status', 'Product URL', 'Image URL', //'Created At'
];

const getFeeSettings = (marketplace) => {
	try {
		const saved = localStorage.getItem(`${marketplace}_fee_settings`);
		return saved ? JSON.parse(saved) : {};
	} catch { return {}; }
};

const calculateDealMetrics = (deal) => {
	const data = deal.marketplace_data || {};
	const sourcePrice = parseFloat(deal.source_price || 0);
	const marketplace = deal.marketplace || '';

	const itemWeight = data.weight || deal.weight || 1;
	const feeSettings = getFeeSettings(marketplace);
	const inboundShippingPerLb = feeSettings.inboundShipping || 0;
	const inboundShippingCost = inboundShippingPerLb * itemWeight;

	if (marketplace === 'amazon') {
		const azPrice = data.az_price || 0;
		const fbaFee = data.fba_fee || 0; // default fulfillment = fba
		const refFeePerc = data.ref_fee_perc || 0.15;
		const refFee = azPrice * refFeePerc;
		const placementFees = feeSettings.placementFees || 0;
		const inboundShipping = inboundShippingCost;
		const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
		const otherCosts = feeSettings.otherCosts || 0;

		const totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTaxAmount + otherCosts;
		const profit = azPrice - sourcePrice - totalFees;
		const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

		return { sellPrice: azPrice, totalFees, profit, roi };

	} else if (marketplace === 'walmart') {
		const wmPrice = data.price_value || 0;

		let wfsFee = 0;
		if (data.dimensions && itemWeight) {
			wfsFee = calculateWFSFee(
				data.dimensions,
				itemWeight,
				{
					isApparel: data.category?.toLowerCase().includes("clothing") ||
						data.category?.toLowerCase().includes("apparel") ||
						data.sub_category?.toLowerCase().includes("clothing") ||
						data.sub_category?.toLowerCase().includes("apparel"),
					retailPrice: wmPrice
				}
			);
		} else {
			wfsFee = 3.45;
		}

		const refFee = calculateReferralFee(wmPrice, data.category, data.sub_category);
		const inboundShipping = inboundShippingCost;
		const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
		const otherCosts = feeSettings.otherCosts || 0;

		const totalFees = wfsFee + refFee + inboundShipping + salesTaxAmount + otherCosts;
		const profit = wmPrice - sourcePrice - totalFees;
		const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

		return { sellPrice: wmPrice, totalFees, profit, roi };

	} else if (marketplace === 'ebay') {
		const ebayPrice = data.lowest_price || 0;
		const totalSale = ebayPrice;

		const categoryRates = {
			'most': 0.136, 'books': 0.153, 'movies': 0.153, 'music': 0.153,
			'coins': 0.1325, 'bullion': totalSale <= 7500 ? 0.136 : 0.07,
			'handbags': totalSale <= 2000 ? 0.15 : 0.09, 'collectibles': 0.1325,
			'jewelry': totalSale <= 5000 ? 0.15 : 0.09, 'watches': 0.15,
			'guitars': 0.067, 'athletic_shoes': totalSale >= 150 ? 0.08 : 0.136, 'nfts': 0.05
		};

		const categoryFromData = (data.category || 'most').toLowerCase();
		const category = categoryRates[categoryFromData] ? categoryFromData : 'most';
		const baseFeeRate = categoryRates[category] || 0.136;

		const storeSubscription = feeSettings.storeSubscription || 'none';
		const isTopRated = feeSettings.topRated || false;

		const storeDiscounts = { 'none': 0, 'basic': 0.066, 'premium': 0.088, 'anchor': 0.102, 'enterprise': 0.110 };
		const storeDiscount = storeDiscounts[storeSubscription] || 0;
		const topRatedDiscount = isTopRated ? 0.10 : 0;
		const totalDiscount = storeDiscount + topRatedDiscount;

		let finalValueFee = 0;
		if (category === 'watches') {
			if (totalSale <= 1000) finalValueFee = totalSale * 0.15;
			else if (totalSale <= 7500) finalValueFee = (1000 * 0.15) + ((totalSale - 1000) * 0.065);
			else finalValueFee = (1000 * 0.15) + (6500 * 0.065) + ((totalSale - 7500) * 0.03);
			finalValueFee *= (1 - totalDiscount);
		} else if (category === 'bullion' && totalSale > 7500) {
			finalValueFee = totalSale * 0.07 * (1 - totalDiscount);
		} else {
			if (totalSale <= 7500) finalValueFee = totalSale * baseFeeRate;
			else finalValueFee = (7500 * baseFeeRate) + ((totalSale - 7500) * 0.0235);
			finalValueFee *= (1 - totalDiscount);
		}

		let perOrderFee = (category === 'athletic_shoes' && totalSale >= 150) ? 0 : (totalSale <= 10 ? 0.30 : 0.40);
		const paymentProcessingFee = (totalSale * 0.0235) + 0.30;
		const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
		const packagingCost = feeSettings.packagingCost || 0;
		const otherCosts = feeSettings.otherCosts || 0;

		const totalFees = finalValueFee + perOrderFee + paymentProcessingFee + salesTaxAmount + packagingCost + otherCosts;
		const profit = ebayPrice - sourcePrice - totalFees;
		const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

		return { sellPrice: ebayPrice, totalFees, profit, roi };
	}

	return { sellPrice: 0, totalFees: 0, profit: 0, roi: 0 };
};

const escapeCsvField = (field) => {
	const str = String(field ?? '');
	if (str.includes(',') || str.includes('"') || str.includes('\n')) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
};

const buildRow = (deal, item, config) => {
	const data = deal.marketplace_data || {};
	const sourcePrice = parseFloat(deal.source_price || 0);
	const upc = data.upc || data.ean || data.gtin || deal.upc || '';
	const metrics = calculateDealMetrics(deal);

	const commonFields = [
		deal.marketplace || '',
		deal.product_id || data.asin || '',
		deal.title || data.title || data.name || '',
		upc,
		sourcePrice.toFixed(2),
		metrics.sellPrice.toFixed(2),
		metrics.totalFees.toFixed(2),
		metrics.profit.toFixed(2),
		metrics.roi.toFixed(1),
		deal.gated_status || '',
		deal.product_url || '',
		deal.img || '',
		// deal.created_at || '',
	];

	return [...config.extraFields(deal, item), ...commonFields];
};

const fetchAllItems = async (listUrl, listKey) => {
	const allItems = [];
	let page = 1;
	const pageSize = 100;

	while (true) {
		const response = await authService.authenticatedFetch(
			`${listUrl}?page=${page}&page_size=${pageSize}`
		);
		if (!response.ok) break;

		const data = await response.json();
		const items = data[listKey] || [];
		allItems.push(...items);

		if (!data.has_more || items.length < pageSize) break;
		page++;
	}

	return allItems;
};

const MegaExport = ({ scanType }) => {
	const [showModal, setShowModal] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [error, setError] = useState(null);
	const [phase, setPhase] = useState('');
	const [progress, setProgress] = useState({ current: 0, total: 0, currentName: '', totalDeals: 0 });

	const config = SCAN_TYPE_CONFIG[scanType];

	const handleExport = async () => {
		setExporting(true);
		setError(null);
		setPhase('list');
		setProgress({ current: 0, total: 0, currentName: '', totalDeals: 0 });

		try {
			const allItems = await fetchAllItems(config.listUrl, config.listKey);

			if (allItems.length === 0) {
				setError(`No ${config.itemNoun} found.`);
				setExporting(false);
				return;
			}

			setPhase('deals');
			setProgress({ current: 0, total: allItems.length, currentName: '', totalDeals: 0 });

			const allRows = [];

			for (let i = 0; i < allItems.length; i++) {
				const item = allItems[i];
				const name = config.itemName(item);
				setProgress(prev => ({ ...prev, current: i + 1, currentName: name }));

				try {
					const response = await authService.authenticatedFetch(config.dealsUrl(item));
					if (!response.ok) continue;

					const data = await response.json();
					const deals = data[config.dealsKey] || [];

					for (const deal of deals) {
						allRows.push(buildRow(deal, item, config));
					}

					setProgress(prev => ({ ...prev, totalDeals: allRows.length }));
				} catch {
					// Skip failed fetches
				}
			}

			if (allRows.length === 0) {
				setError('No deals found to export.');
				setExporting(false);
				return;
			}

			const headers = [...config.extraHeaders, ...COMMON_HEADERS];
			const csvLines = [
				headers.map(escapeCsvField).join(','),
				...allRows.map(row => row.map(escapeCsvField).join(','))
			];
			const csvContent = csvLines.join('\n');

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const downloadUrl = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = downloadUrl;
			const today = new Date().toISOString().split('T')[0];
			a.download = `${config.filename}_${today}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(downloadUrl);

			setShowModal(false);
		} catch (err) {
			setError(err.message || 'Export failed. Please try again.');
		} finally {
			setExporting(false);
		}
	};

	const handleClose = () => {
		if (exporting) return;
		setShowModal(false);
		setError(null);
	};

	const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

	return (
		<>
			<button
				onClick={() => setShowModal(true)}
				className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
			>
				<Download size={20} />
				Export All Data
			</button>

			{showModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
								<Download className="text-teal-500" size={22} />
								Export {config.label} Data
							</h3>
							{!exporting && (
								<button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
									<X size={20} />
								</button>
							)}
						</div>

						{!exporting ? (
							<>
								<p className="text-gray-500 text-sm mb-5">
									Export all your {config.label.toLowerCase()} deals as a CSV file.
									This will fetch all {config.itemNoun} and their deals and combine them into one file.
								</p>

								{error && (
									<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
										{error}
									</div>
								)}

								<div className="flex gap-3">
									<button
										onClick={handleExport}
										className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
									>
										<Download size={18} />
										Export CSV
									</button>
									<button
										onClick={handleClose}
										className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition-all duration-200"
									>
										Cancel
									</button>
								</div>
							</>
						) : (
							<div>
								{phase === 'list' ? (
									<div className="flex items-center gap-2 text-sm text-gray-600">
										<Loader2 className="animate-spin text-teal-500" size={16} />
										Loading {config.itemNoun}...
									</div>
								) : (
									<>
										<div className="mb-3">
											<div className="flex items-center justify-between text-sm mb-1.5">
												<span className="text-gray-600">
													Fetching deals ({progress.current}/{progress.total})
												</span>
												<span className="font-semibold text-teal-600">{pct}%</span>
											</div>
											<div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
												<div
													className="bg-gradient-to-r from-teal-500 to-emerald-500 h-3 rounded-full transition-all duration-300 ease-out"
													style={{ width: `${pct}%` }}
												/>
											</div>
										</div>
										<div className="flex items-center justify-between text-xs text-gray-400">
											<span className="truncate flex items-center gap-1.5 max-w-[60%]">
												<Loader2 className="animate-spin shrink-0" size={12} />
												{progress.currentName}
											</span>
											<span>{progress.totalDeals.toLocaleString()} deals collected</span>
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default MegaExport;
